import { type Href } from "expo-router"

import { H4, H5 } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { BFLink } from "@/components/buttons/Links"
import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import {
  loanAppLoanWizard,
  loanRehabBudget,
  loanSoftCreditPull,
} from "@/screens/loans/utils/LoanUtils"
import { CreditRepairWidget } from "@/screens/loans/components/CreditRepairWidget"
import { BudgetReviewStatus } from "@/screens/rehab/utils/Rehab"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { BFPressable } from "@/components/buttons/CommonButtons"
import { cn } from "@/lib/utils"
import { DownloanAppWidget } from "@/screens/loans/components/DownloadAppWidget"
import { OrchardWidget } from "@/screens/loans/components/OrchardWidget"

interface LoanTaskListItemProps {
  title: string
  actionPath?: Href
  actionUrl?: string
  ctaText?: string
}

const LoanTaskListItem = ({
  title,
  actionPath,
  actionUrl,
  ctaText = "Complete Now ›",
}: LoanTaskListItemProps) => {
  return (
    <BFPressable title={title} testID={`LoanTaskListItemPressable-${title}`}>
      {({ pressed }) => {
        return (
          <Horizontal
            className={cn(
              "justify-between p-5 bg-white border rounded-md border-light-grey",
              pressed && "opacity-50"
            )}
          >
            <H5>{title}</H5>
            <BFLink
              size="sm"
              testId={`LoanTaskListItemLink-${title}`}
              url={actionUrl}
              path={actionPath}
              text={ctaText}
            />
          </Horizontal>
        )
      }}
    </BFPressable>
  )
}

const RehabBudgetTask = ({ title, actionPath }: LoanTaskListItemProps) => {
  const isDesktop = useIsDesktop()
  const { loanApplication } = useLoanContext()
  const { rehabBudget, featureIsEnabled } = useRehabBudgetingFeature(
    loanApplication,
    isDesktop
  )

  if (!featureIsEnabled) {
    return null
  }

  // TODO: We may just not want to display the task if the budget is in review for now. This was in the last version.
  const ctaText =
    rehabBudget?.status === BudgetReviewStatus.IN_REVIEW ? "View ›" : undefined

  return (
    <LoanTaskListItem title={title} actionPath={actionPath} ctaText={ctaText} />
  )
}

export const LoanTaskList = ({ loanId }: { loanId: string }) => {
  const { data: taskListData } = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return {
        uploadPortalLink: data?.uploadPortalLink || undefined,
        tasks: data?.tasks || [],
      }
    },
  })

  if (!taskListData) {
    return null
  }

  const hasVisibleTasks =
    taskListData.tasks.filter((task) => task.isVisible).length > 0

  if (!hasVisibleTasks) {
    return null
  }

  return (
    <Vertical className={"gap-1 pb-2"}>
      <Horizontal>
        <H4 color={"default"}>To Do</H4>
      </Horizontal>
      <Vertical className="w-full gap-2">
        {taskListData.tasks.map((task) => {
          if (!task.isVisible) {
            return null
          }

          if (task.taskType === "COMPLETE_INQUIRY") {
            return (
              <LoanTaskListItem
                key={task.taskType}
                title="Continue Inquiry"
                actionPath={loanAppLoanWizard(loanId)}
              />
            )
          }

          if (task.taskType === "SOFT_CREDIT_PULL") {
            return (
              <LoanTaskListItem
                key={task.taskType}
                title="Soft Credit Pull"
                actionPath={loanSoftCreditPull(loanId)}
              />
            )
          }

          if (task.taskType === "COMPLETE_REHAB_SOW") {
            return (
              <RehabBudgetTask
                key={task.taskType}
                title="Rehab SOW Budget"
                actionPath={loanRehabBudget(loanId)}
              />
            )
          }

          if (task.taskType === "UPLOAD_DOCUMENTS") {
            return (
              <LoanTaskListItem
                key={task.taskType}
                title="Upload Documents"
                actionUrl={taskListData.uploadPortalLink || "#"}
              />
            )
          }

          return null
        })}
        <CreditRepairWidget />
        <OrchardWidget />
        <DownloanAppWidget />
      </Vertical>
    </Vertical>
  )
}
